import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/utils";
import { Badge } from "@/components/ui/data";
import { CalendarDays, CircleDollarSign, ReceiptText, TrendingDown, ArrowUpRight, ArrowDownRight } from "lucide-react";

interface Summary {
  revenueCents: number;
  bookings: number;
  completed: number;
  cancellations: number;
  noShows: number;
  averageTicketCents: number | null;
  previousRevenueCents: number;
  previousBookings: number;
  change: { revenuePct: number | null; bookingsPct: number | null };
}

export function DashboardPage() {
  const [locations, setLocations] = useState<Array<{ id: number; name: string; currency: string; active: boolean }>>([]);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [series, setSeries] = useState<Array<{ day: string; bookings: number; revenueCents: number }>>([]);
  const [services, setServices] = useState<Array<{ serviceId: number; serviceName: string; revenueCents: number; units: number }>>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Array<{ id: number; name: string; currency: string; active: boolean }>>("/api/v1/locations").then((rows) => {
      const active = rows.filter((row) => row.active);
      setLocations(active);
      if (active[0]) setLocationId(active[0].id);
    }).catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (locationId == null) return;
    Promise.all([
      api.get<Summary>(`/api/v1/analytics/summary?locationId=${locationId}`),
      api.get<Array<{ day: string; bookings: number; revenueCents: number }>>(`/api/v1/analytics/timeseries?locationId=${locationId}`),
      api.get<Array<{ serviceId: number; serviceName: string; revenueCents: number; units: number }>>(`/api/v1/analytics/services?locationId=${locationId}`),
    ]).then(([s, ts, sv]) => {
      setSummary(s);
      setSeries(ts);
      setServices(sv);
    }).catch((e) => setError(e.message));
  }, [locationId]);

  const currency = locations.find((l) => l.id === locationId)?.currency ?? "";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div><p className="text-sm font-medium text-primary">Operations overview</p><h1 className="page-heading mt-1">Good to see you</h1><p className="mt-1 text-sm text-muted-foreground">Your latest performance and booking activity.</p></div>
        {locations.length > 1 && (
          <select
            value={locationId ?? ""}
            onChange={(e) => setLocationId(Number(e.target.value))}
            className="h-10 rounded-lg border bg-white px-3 text-sm shadow-sm"
          >
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        )}
      </div>
      {error && <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
      {summary && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Kpi icon={CircleDollarSign} label="Revenue" value={formatMoney(summary.revenueCents, currency)} delta={summary.change.revenuePct} />
          <Kpi icon={CalendarDays} label="Bookings" value={String(summary.bookings)} delta={summary.change.bookingsPct} />
          <Kpi icon={ReceiptText} label="Average ticket" value={summary.averageTicketCents == null ? "—" : formatMoney(summary.averageTicketCents, currency)} />
          <Kpi icon={TrendingDown} label="Cancellation rate" value={pct(summary.cancellations, summary.bookings)} />
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Revenue trend</CardTitle>
            <CardDescription>Daily completed revenue vs booking count.</CardDescription>
          </CardHeader>
          <CardContent>
            <SeriesChart series={series} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Service mix</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {services.length === 0 && <p className="text-sm text-muted-foreground">No completed bookings yet.</p>}
            {services.map((s) => (
              <div key={s.serviceId} className="flex items-center justify-between rounded-lg border border-transparent px-2 py-2.5 text-sm hover:border-border hover:bg-muted/40">
                <span className="font-medium">{s.serviceName}</span>
                <span className="tabular-nums text-muted-foreground">{formatMoney(s.revenueCents, currency)} · {s.units}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Badge>Live</Badge><span>Metrics refresh when you open this workspace.</span></div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, delta }: { icon: typeof CalendarDays; label: string; value: string; delta?: number | null }) {
  return (
    <Card>
      <CardContent className="p-5 md:p-5">
        <div className="flex items-start justify-between"><p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</p><span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/[0.07] text-primary"><Icon className="h-4 w-4" /></span></div>
        <p className="mt-3 text-2xl font-semibold tracking-[-0.035em] tabular-nums">{value}</p>
        {delta != null && (
          <p className={`mt-2 flex items-center gap-1 text-xs font-medium ${delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {delta >= 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />} {Math.abs(delta).toFixed(1)}% vs prior period
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function pct(n: number, d: number): string {
  if (!d) return "0%";
  return `${Math.round((n / d) * 1000) / 10}%`;
}

function SeriesChart({ series }: { series: Array<{ day: string; bookings: number; revenueCents: number }> }) {
  if (series.length === 0) {
    return <p className="text-sm text-muted-foreground">No data yet for the selected window.</p>;
  }
  const maxRev = Math.max(1, ...series.map((s) => s.revenueCents));
  return (
    <div className="flex h-40 items-end gap-1">
      {series.map((s) => {
        const h = Math.max(2, Math.round((s.revenueCents / maxRev) * 100));
        return (
          <div key={s.day} title={`${s.day}: ${s.bookings} bookings`}
            className="flex-1 rounded-t bg-primary/70 hover:bg-primary"
            style={{ height: `${h}%` }} />
        );
      })}
    </div>
  );
}
